import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { type PaginatorState } from 'primeng/paginator';
import { DatePicker } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';

import { AuthService } from '@/core/auth/auth.service';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { MapaUfCard } from '@/shared/components/mapa-uf-card/mapa-uf-card';
import { PainelLateral } from '@/shared/components/painel-lateral/painel-lateral';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata } from '@/shared/components/layout/custom-table/custom-table.component';
import { DataExtensoPipe } from '@/shared/pipes/data-extenso.pipe';
import { MedicoControleAdministrativoFeatureService } from '../mapa-brasil/medico-controle-administrativo.service';
import { MedicoPendenciaResponse } from '@/core/api/sicom-api/registro/medico/pendencia/medico-pendencia.model';
import { UfSelecaoGestaoService } from '../uf-selecao-gestao.service';

type MedicoAtivoLinha = MedicoPendenciaResponse & {
    situacao?: string;
    nascimento?: string;
};

@Component({
    selector: 'app-medicos-ativos-data',
    standalone: true,
    imports: [CommonModule, FormsModule, CardModule, SkeletonModule, DatePicker, ButtonModule, SelectEstado, MapaUfCard, PainelLateral, CardContent, CustomTableComponent, DataExtensoPipe],
    templateUrl: './medicos-ativos-data.html',
    styleUrl: './medicos-ativos-data.scss'
})
export class MedicosAtivosData implements OnInit {
    ufSelecionada: string | null = null;
    ufsPermitidas: string[] | null = null;
    dataCorte: Date | null = new Date();
    /** Não faz sentido apurar ativos em uma data futura: limita o calendário a hoje. */
    readonly hoje = new Date();

    total: number | null = null;
    linhas: MedicoAtivoLinha[] = [];
    carregandoTotal = false;
    carregandoLista = false;
    first = 0;
    rows = 100;
    totalRecords = 0;
    ufConsulta: string | null = null;
    /** A relação detalhada (estatísticas) só é carregada/exibida quando o usuário clica no botão do card. */
    mostrarLista = false;

    readonly tabelaMetadata: TableMetadata = {
        title: 'nmSolicitante',
        tag: 'ufSgUf',
        headers: {
            ufSgUf: { type: 'text', value: 'UF', hidden: false, default: '-' },
            nuCrm: { type: 'text', value: 'CRM', hidden: false, default: '-' },
            nmSolicitante: { type: 'text', value: 'Nome', hidden: false, default: '-' },
            situacao: { type: 'text', value: 'Situação', hidden: false, default: '-' },
            nascimento: { type: 'text', value: 'Nascimento', hidden: false, default: '-' }
        }
    };

    private dataConsulta = '';
    private ufTokenRestrita: string | null = null;
    private readonly mudanca$ = new Subject<void>();
    private readonly listaChange$ = new Subject<void>();

    constructor(
        private feature: MedicoControleAdministrativoFeatureService,
        private destroyRef: DestroyRef,
        private authService: AuthService,
        private ufSelecaoGestao: UfSelecaoGestaoService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.configurarUfPorToken();
        this.aplicar();
    }

    onUfSelecionadaChange(uf?: string | null): void {
        this.ufSelecionada = this.normalizarUf(uf);
        this.ufSelecaoGestao.definir(this.ufSelecionada);
    }

    aplicar(): void {
        if (!this.dataCorte) {
            return;
        }

        this.ufConsulta = this.ufSelecionada;
        this.dataConsulta = this.formatarIso(this.dataCorte);
        this.first = 0;
        this.mostrarLista = false;
        this.carregarTotal();
    }

    /** Carrega e exibe a relação detalhada (dados estatísticos) para os filtros aplicados. */
    mostrarDetalhes(): void {
        this.mostrarLista = true;
        this.first = 0;
        this.carregarLista();
    }

    onPageChange(event: PaginatorState): void {
        this.first = event.first ?? 0;
        this.rows = event.rows ?? this.rows;
        this.carregarLista();
    }

    get dataFormatada(): string {
        return this.dataConsulta || this.formatarIso(this.dataCorte ?? this.hoje);
    }

    private carregarTotal(): void {
        this.mudanca$.next();
        this.carregandoTotal = true;
        this.total = null;
        this.feature
            .consultarTotalMedicosAtivosNaData(this.ufConsulta ?? undefined, this.dataConsulta)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.mudanca$),
                // markForCheck: estado assíncrono gravado em campos não-signal sob layout OnPush não seria renderizado sem marcar o caminho como dirty.
                finalize(() => {
                    this.carregandoTotal = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (valor) => (this.total = valor ?? 0),
                error: () => (this.total = 0)
            });
    }

    private carregarLista(): void {
        this.listaChange$.next();
        const page = this.rows > 0 ? Math.floor(this.first / this.rows) : 0;
        this.carregandoLista = true;
        this.feature
            .consultarMedicosAtivosNaData(this.ufConsulta ?? undefined, this.dataConsulta, page, this.rows)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                takeUntil(this.listaChange$),
                finalize(() => {
                    this.carregandoLista = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (resp) => {
                    this.linhas = (resp?.content ?? []).map((l) => ({
                        ...l,
                        situacao: l.dsSituacao || l.inSituacao,
                        nascimento: l.informacaoComplementar || '-'
                    }));
                    this.totalRecords = resp?.totalElements ?? 0;
                },
                error: () => {
                    this.linhas = [];
                    this.totalRecords = 0;
                }
            });
    }

    private formatarIso(d: Date): string {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    }

    private configurarUfPorToken(): void {
        const ufToken = this.authService.ufDoUsuario()?.trim().toUpperCase() ?? '';
        if (/^[A-Z]{2}$/.test(ufToken) && ufToken !== 'BR') {
            this.ufTokenRestrita = ufToken;
            this.ufsPermitidas = [ufToken];
            this.ufSelecionada = ufToken;
            this.ufSelecaoGestao.definir(ufToken);
        } else {
            this.ufTokenRestrita = null;
            this.ufsPermitidas = null;
            this.ufSelecionada = this.ufSelecaoGestao.uf();
        }
    }

    private normalizarUf(uf?: string | null): string | null {
        const v = String(uf ?? '')
            .trim()
            .toUpperCase();
        if (!v || v === 'BR' || !/^[A-Z]{2}$/.test(v)) return this.ufTokenRestrita;
        if (this.ufTokenRestrita && v !== this.ufTokenRestrita) return this.ufTokenRestrita;
        return v;
    }
}
